/** Lib Imports */
import { Button } from "primereact/button";
import PropTypes from "prop-types";

/** ENUM */
import UserRoles from "../../enums/UserRoles.ENUM";
import JobRequisitionStatus from "../../enums/JobRequisitionStatus";

const JobRequisitionActions = ({
    row,
    onEdit,
    onDelete,
    onAction,
    roleId,
    hasEditPermission,
    hasDeletePermission,
}) => {
    const status = row.status || row.requisitionStatusId;
    const level = row.approvalLevel;

    // Roles
    const isInterviewer = roleId === UserRoles.INTERVIEWER;
    const isHR = roleId === UserRoles.HR;
    const isAdmin = roleId === UserRoles.ADMIN;

    // Helper functions to determine permissions
    const canInterviewerEdit = () => isInterviewer && (status === JobRequisitionStatus.DRAFT || status === JobRequisitionStatus.REJECTED);
    const canInterviewerSubmit = () => isInterviewer && status === JobRequisitionStatus.DRAFT;
    const canInterviewerResubmit = () => isInterviewer && status === JobRequisitionStatus.REJECTED;
    const canInterviewerCancel = () => isInterviewer && (status === JobRequisitionStatus.DRAFT || status === JobRequisitionStatus.PENDING_APPROVAL);

    const canHRSubmit = () => isHR && status === JobRequisitionStatus.DRAFT;
    const canHRApprove = () => isHR && status === JobRequisitionStatus.PENDING_APPROVAL && level === 2;
    const canHRHold = () => isHR && status === JobRequisitionStatus.PENDING_APPROVAL && level === 2;
    const canHRResume = () => isHR && status === JobRequisitionStatus.ON_HOLD && level === 2;
    const canHRCancel = () => isHR && (status === JobRequisitionStatus.DRAFT || (status === JobRequisitionStatus.PENDING_APPROVAL && level === 2) || (status === JobRequisitionStatus.ON_HOLD && level === 2));
    const canHRUpdate = () => isHR && status === JobRequisitionStatus.APPROVED;

    const canAdminApprove = () => isAdmin && status === JobRequisitionStatus.PENDING_APPROVAL && level === 3;
    const canAdminReject = () => isAdmin && ((status === JobRequisitionStatus.PENDING_APPROVAL && level === 3) || (status === JobRequisitionStatus.ON_HOLD && level === 3));
    const canAdminHold = () => isAdmin && status === JobRequisitionStatus.PENDING_APPROVAL && level === 3;
    const canAdminResume = () => isAdmin && status === JobRequisitionStatus.ON_HOLD && level === 3;

    // Helper function to determine if edit icon should be pencil or eye
    const getEditIcon = () => canInterviewerEdit() || (isAdmin && hasEditPermission?.isEditable) ? "pi pi-pencil" : "pi pi-eye";
    const getEditTooltip = () => canInterviewerEdit() || (isAdmin && hasEditPermission?.isEditable) ? "Edit" : "View";

    return (
        <div className="flex gap-2">
            {/* View/Edit Button */}
            <Button
                icon={getEditIcon()}
                className="p-button-text p-button-sm"
                onClick={() => onEdit(row)}
                tooltip={getEditTooltip()}
                data-pr-position="bottom"
            />

            {/* Submit / Resubmit (Interviewer/HR) */}
            {(canInterviewerSubmit() || canHRSubmit()) && (
                <Button
                    icon="pi pi-send"
                    className="p-button-text p-button-sm"
                    onClick={() => onAction(row, JobRequisitionStatus.PENDING_APPROVAL, "Submit")}
                    tooltip="Submit"
                    data-pr-position="bottom"
                />
            )}

            {canInterviewerResubmit() && (
                <Button
                    icon="pi pi-refresh"
                    className="p-button-text p-button-sm"
                    onClick={() => onAction(row, JobRequisitionStatus.PENDING_APPROVAL, "Resubmit")}
                    tooltip="Resubmit"
                    data-pr-position="bottom"
                />
            )}

            {/* Approve */}
            {(canHRApprove() || canAdminApprove()) && (
                <Button
                    icon="pi pi-check"
                    className="p-button-text p-button-sm p-button-success"
                    onClick={() => onAction(row, (canHRApprove() ? JobRequisitionStatus.PENDING_APPROVAL : JobRequisitionStatus.APPROVED), "Approve")}
                    tooltip={canHRApprove() ? "Approve (Send to Admin)" : "Approve"}
                    data-pr-position="bottom"
                />
            )}

            {/* Reject (Admin) */}
            {canAdminReject() && (
                <Button
                    icon="pi pi-times-circle"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() => onAction(row, JobRequisitionStatus.REJECTED, "Reject")}
                    tooltip="Reject"
                    data-pr-position="bottom"
                />
            )}

            {/* Hold */}
            {(canHRHold() || canAdminHold()) && (
                <Button
                    icon="pi pi-pause-circle"
                    className="p-button-text p-button-sm p-button-warning"
                    onClick={() => onAction(row, JobRequisitionStatus.ON_HOLD, "Hold")}
                    tooltip="Hold"
                    data-pr-position="bottom"
                />
            )}

            {/* Resume */}
            {(canHRResume() || canAdminResume()) && (
                <Button
                    icon="pi pi-play-circle"
                    className="p-button-text p-button-sm p-button-info"
                    onClick={() => onAction(row, JobRequisitionStatus.PENDING_APPROVAL, "Resume")}
                    tooltip="Resume"
                    data-pr-position="bottom"
                />
            )}

            {/* Cancel (Interviewer/HR) */}
            {(canInterviewerCancel() || canHRCancel()) && (
                <Button
                    icon="pi pi-ban"
                    className="p-button-text p-button-sm p-button-secondary"
                    onClick={() => onAction(row, JobRequisitionStatus.CANCELLED, "Cancel")}
                    tooltip="Cancel"
                    data-pr-position="bottom"
                />
            )}

            {/* Update (HR) */}
            {canHRUpdate() && (
                <Button
                    icon="pi pi-link"
                    className="p-button-text p-button-sm"
                    onClick={() => onEdit(row)}
                    tooltip="Update (Bind Job Opening)"
                    data-pr-position="bottom"
                />
            )}
        </div>
    );

};

JobRequisitionActions.propTypes = {
    row: PropTypes.shape({
        departmentId: PropTypes.number,
        departmentName: PropTypes.string,
        jobRequisitionId: PropTypes.number,
        requisitionStatusId: PropTypes.number,
        approvalLevel: PropTypes.number,
        status: PropTypes.number,

    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAction: PropTypes.func.isRequired,
    roleId: PropTypes.number.isRequired,
    hasEditPermission: PropTypes.shape({
        isEditable: PropTypes.bool,
        isViewOnly: PropTypes.bool,
    }),
    hasDeletePermission: PropTypes.shape({
        hasAccess: PropTypes.bool,
    }),
};

export default JobRequisitionActions;